import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

const EDGE_FUNCTION_URL = 'https://ogicjlqgeilqbiindjzs.supabase.co/functions/v1/verify-client-email';

export default function PortalLoginPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;

    setLoading(true);
    setError('');

    try {
      const res = await fetch(EDGE_FUNCTION_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim() }),
      });

      const data = await res.json();

      if (data.found) {
        // Log field names for debugging
        console.log('Airtable field names:', data._debug_fields);
        console.log('Owner name:', data.ownerName);
        console.log('Websites:', data.websites);
        // Store the full portal session (owner + all websites)
        sessionStorage.setItem('portal_session', JSON.stringify({
          ownerName: data.ownerName,
          email: data.email,
          websites: data.websites,
        }));
        navigate('/portal');
      } else {
        setError('No account found with that email. Please check and try again.');
      }
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/* Header */}
      <header className="bg-[#1a1a1a] relative overflow-hidden">
        <div className="absolute top-0 left-0 w-1 h-full bg-[#F5C518]"></div>
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage:
              'linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)',
            backgroundSize: '60px 60px',
          }}
        ></div>
        <div className="relative z-10 max-w-6xl mx-auto px-6 sm:px-10 py-12">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-white/40 hover:text-[#F5C518] text-xs tracking-widest uppercase mb-10 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-arrow-left-line text-sm"></i>
            Back to Home
          </button>
          <p className="text-[#F5C518] text-xs tracking-[0.25em] uppercase font-medium mb-3">
            Client Portal
          </p>
          <h1 className="font-display text-white text-4xl sm:text-5xl tracking-wide leading-tight mb-3">
            SIGN IN
          </h1>
          <p className="text-white/40 text-sm">Enter your email to access your portal</p>
        </div>
      </header>

      {/* Content */}
      <main className="flex-1 flex items-center justify-center px-6 py-20">
        <div className="w-full max-w-md">
          <div className="bg-white border border-[#eee] p-10 rounded-sm">
            <div className="mb-8">
              <div className="w-12 h-12 flex items-center justify-center bg-[#F5C518]/10 rounded-sm mb-5">
                <i className="ri-user-line text-2xl text-[#F5C518]"></i>
              </div>
              <h2 className="font-display text-[#1a1a1a] text-2xl tracking-wide mb-2">
                CLIENT ACCESS
              </h2>
              <p className="text-[#666] text-sm leading-relaxed">
                Enter the email address associated with your project to access your portal.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label htmlFor="email" className="block text-sm text-[#666] mb-2">
                  Email Address <span className="text-[#F5C518]">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); setError(''); }}
                  placeholder="you@example.com"
                  required
                  className="w-full px-4 py-3 border border-[#ddd] rounded-sm text-sm focus:outline-none focus:border-[#F5C518] transition-colors"
                />
              </div>

              {error && (
                <div className="flex items-center gap-2 bg-red-50 border border-red-100 px-4 py-3 rounded-sm">
                  <i className="ri-error-warning-line text-red-400 text-sm shrink-0"></i>
                  <p className="text-red-500 text-xs">{error}</p>
                </div>
              )}

              <button
                type="submit"
                disabled={loading || !email.trim()}
                className={`w-full py-3.5 rounded-sm font-medium text-sm tracking-wide transition-all whitespace-nowrap ${
                  loading || !email.trim()
                    ? 'bg-[#eee] text-[#bbb] cursor-not-allowed'
                    : 'bg-[#F5C518] text-[#1a1a1a] hover:bg-[#d4a814] cursor-pointer'
                }`}
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-loader-4-line animate-spin"></i>
                    Checking...
                  </span>
                ) : (
                  'Access My Portal'
                )}
              </button>
            </form>

            <p className="text-xs text-[#bbb] text-center mt-6">
              Only existing clients can access the portal
            </p>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-[#FAFAF8] border-t border-[#eee]">
        <div className="max-w-6xl mx-auto px-6 sm:px-10 py-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <span className="font-display text-sm tracking-[0.1em] text-[#1a1a1a]">
            YASMINE CREATES
          </span>
          <p className="text-xs text-[#999]">&copy; 2025 Yasmine Creates. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
